if (!customElements.get('press-and-awards')) {
  class PressAndAwards extends HTMLElement {
    connectedCallback() {
      this.categoryTablist = this.querySelector('.press-awards__categories');
      this.navArea = this.querySelector('.press-awards__nav');
      this.categoryButtons = this.querySelectorAll('.press-awards__category-btn');
      this.panels = this.querySelectorAll('.press-awards__panel');
      this.statusRegion = this.querySelector('.press-awards__status');
      this.mobileCategorySelect = this.querySelector('.press-awards__mobile-category');
      this.mobileYearSelect = this.querySelector('.press-awards__mobile-year');
      this.mobileYearLabel = this.querySelector('.press-awards__mobile-year-label');
      this.activeCategoryButton = null;
      this.activePanel = null;
      this.activeYear = 'all';

      this.handleNavClickBound = this.handleNavClick.bind(this);
      this.handleNavKeydownBound = this.handleNavKeydown.bind(this);
      this.handleYearKeydownBound = this.handleYearKeydown.bind(this);
      this.handleMobileCategoryChangeBound = this.handleMobileCategoryChange.bind(this);
      this.handleMobileYearChangeBound = this.handleMobileYearChange.bind(this);
      this.handleBlockSelectBound = this.handleBlockSelect.bind(this);

      if (!this.categoryButtons.length) return;

      this.init();

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelectBound);
      }
    }

    disconnectedCallback() {
      if (this.handleBlockSelectBound) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelectBound);
      }
    }

    init() {
      this.initImageFade();
      this.buildMobileNav();
      this.buildYearNavigation();

      const activeButton = this.categoryButtons[0];
      this.activateCategory(activeButton);

      if (this.navArea) {
        this.navArea.addEventListener('click', this.handleNavClickBound);
        this.navArea.addEventListener('keydown', this.handleYearKeydownBound);
      }
      this.categoryTablist.addEventListener('keydown', this.handleNavKeydownBound);

      if (this.mobileCategorySelect) {
        this.mobileCategorySelect.addEventListener('change', this.handleMobileCategoryChangeBound);
      }
      if (this.mobileYearSelect) {
        this.mobileYearSelect.addEventListener('change', this.handleMobileYearChangeBound);
      }
    }

    initImageFade() {
      // Enable the fade-in styles (kept off by default so images are never
      // stuck invisible if JS doesn't run).
      this.classList.add('js-fade-images');

      this.querySelectorAll('.press-awards__card-image').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) {
          img.classList.add('is-loaded');
          return;
        }

        const markLoaded = () => img.classList.add('is-loaded');
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      });
    }

    handleBlockSelect(evt) {
      const panel = evt.target.closest('.press-awards__panel');
      if (!panel || !this.contains(panel)) return;

      const categoryButton = this.querySelector(`[aria-controls="${panel.id}"]`);
      if (categoryButton) {
        this.activateCategory(categoryButton);
      }
    }

    buildMobileNav() {
      if (!this.mobileCategorySelect) return;

      this.mobileCategorySelect.innerHTML = '';
      this.categoryButtons.forEach((button) => {
        const option = document.createElement('option');
        option.value = button.id;
        option.textContent = button.querySelector('.press-awards__category-label')?.textContent.trim()
          || button.textContent.trim();
        this.mobileCategorySelect.appendChild(option);
      });
    }

    buildYearNavigation() {
      this.querySelectorAll('.press-awards__category').forEach((categoryEl) => {
        const button = categoryEl.querySelector('.press-awards__category-btn');
        const yearNav = categoryEl.querySelector('[data-year-nav]');
        if (!button || !yearNav) return;

        if (yearNav.querySelectorAll('.press-awards__year-btn').length > 0) {
          if (button.dataset.hasYears !== 'false') {
            PressAndAwards.ensureCategoryChevron(button);
          }
          return;
        }

        const panel = document.getElementById(button.getAttribute('aria-controls'));
        const years = [...panel.querySelectorAll('.press-awards__items [data-year]')]
          .map((el) => el.dataset.year?.trim())
          .filter((year) => year);

        const uniqueYears = PressAndAwards.sortYears([...new Set(years)]);

        if (!uniqueYears.length) {
          button.dataset.hasYears = 'false';
          button.querySelector('.press-awards__category-chevron')?.remove();
          yearNav.remove();
          return;
        }

        button.dataset.hasYears = 'true';
        PressAndAwards.ensureCategoryChevron(button);
        yearNav.hidden = true;
        yearNav.innerHTML = '';
        yearNav.appendChild(PressAndAwards.createYearButton('all', 'All', true));

        uniqueYears.forEach((year) => {
          yearNav.appendChild(PressAndAwards.createYearButton(year, year, false));
        });
      });
    }

    static createYearButton(year, label, isActive) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `press-awards__year-btn${isActive ? ' is-active' : ''}`;
      button.dataset.year = year;
      button.textContent = label;
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      }
      return button;
    }

    static ensureCategoryChevron(button) {
      if (button.querySelector('.press-awards__category-chevron')) return;

      const chevron = document.createElement('span');
      chevron.className = 'press-awards__category-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-down" aria-hidden="true" focusable="false" role="presentation"><path d="m6 9 6 6 6-6"/></svg>';
      button.appendChild(chevron);
      button.classList.add('press-awards__category-btn--has-years');
    }

    static sortYears(years) {
      const numeric = [];
      const text = [];

      years.forEach((year) => {
        if (/^\d+$/.test(year)) {
          numeric.push(year);
        } else {
          text.push(year);
        }
      });

      numeric.sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
      text.sort((a, b) => a.localeCompare(b));

      return [...numeric, ...text];
    }

    handleNavClick(evt) {
      const yearButton = evt.target.closest('.press-awards__year-btn');
      if (yearButton) {
        evt.preventDefault();
        const categoryWrapper = yearButton.closest('.press-awards__category');
        const panel = this.getPanelForCategory(categoryWrapper);
        if (panel && !panel.hidden) {
          this.setYearFilter(yearButton.dataset.year, panel, categoryWrapper);
        }
        return;
      }

      const categoryButton = evt.target.closest('.press-awards__category-btn');
      if (!categoryButton) return;

      this.handleCategoryButtonActivate(categoryButton);
    }

    handleCategoryButtonActivate(categoryButton) {
      if (categoryButton === this.activeCategoryButton && categoryButton.dataset.hasYears === 'true') {
        const yearList = categoryButton.closest('.press-awards__category')?.querySelector('.press-awards__years');
        if (yearList) {
          this.setCategoryYearsExpanded(categoryButton, yearList.hidden);
          return;
        }
      }

      this.activateCategory(categoryButton);
    }

    handleNavKeydown(evt) {
      const categoryButton = evt.target.closest('.press-awards__category-btn');
      if (!categoryButton) return;

      const buttons = Array.from(this.categoryButtons);
      const index = buttons.indexOf(categoryButton);

      switch (evt.key) {
        case ' ':
        case 'Enter':
          evt.preventDefault();
          this.handleCategoryButtonActivate(categoryButton);
          break;
        case 'ArrowUp':
          evt.preventDefault();
          if (index > 0) {
            this.activateCategory(buttons[index - 1]);
            buttons[index - 1].focus();
          }
          break;
        case 'ArrowDown':
          evt.preventDefault();
          if (index < buttons.length - 1) {
            this.activateCategory(buttons[index + 1]);
            buttons[index + 1].focus();
          }
          break;
        case 'Home':
          evt.preventDefault();
          this.activateCategory(buttons[0]);
          buttons[0].focus();
          break;
        case 'End':
          evt.preventDefault();
          this.activateCategory(buttons[buttons.length - 1]);
          buttons[buttons.length - 1].focus();
          break;
        default:
          break;
      }
    }

    handleYearKeydown(evt) {
      const yearButton = evt.target.closest('.press-awards__year-btn');
      if (!yearButton) return;

      const categoryWrapper = yearButton.closest('.press-awards__category');
      const panel = this.getPanelForCategory(categoryWrapper);
      const yearNav = yearButton.closest('[data-year-nav]');
      if (!panel || !yearNav || panel.hidden) return;

      const buttons = Array.from(yearNav.querySelectorAll('.press-awards__year-btn'));
      const index = buttons.indexOf(yearButton);

      switch (evt.key) {
        case 'ArrowUp':
          evt.preventDefault();
          if (index > 0) {
            this.setYearFilter(buttons[index - 1].dataset.year, panel, categoryWrapper);
            buttons[index - 1].focus();
          }
          break;
        case 'ArrowDown':
          evt.preventDefault();
          if (index < buttons.length - 1) {
            this.setYearFilter(buttons[index + 1].dataset.year, panel, categoryWrapper);
            buttons[index + 1].focus();
          }
          break;
        case 'Home':
          evt.preventDefault();
          this.setYearFilter('all', panel, categoryWrapper);
          buttons[0].focus();
          break;
        case 'End':
          evt.preventDefault();
          this.setYearFilter(buttons[buttons.length - 1].dataset.year, panel, categoryWrapper);
          buttons[buttons.length - 1].focus();
          break;
        default:
          break;
      }
    }

    handleMobileCategoryChange() {
      const button = document.getElementById(this.mobileCategorySelect.value);
      if (button) {
        this.activateCategory(button);
      }
    }

    handleMobileYearChange() {
      if (!this.activePanel || !this.activeCategoryButton) return;
      const categoryWrapper = this.activeCategoryButton.closest('.press-awards__category');
      this.setYearFilter(this.mobileYearSelect.value, this.activePanel, categoryWrapper);
    }

    getPanelForCategory(categoryWrapper) {
      const button = categoryWrapper?.querySelector('.press-awards__category-btn');
      if (!button) return null;
      return document.getElementById(button.getAttribute('aria-controls'));
    }

    setCategoryYearsExpanded(button, expanded) {
      const wrapper = button.closest('.press-awards__category');
      const yearList = wrapper?.querySelector('.press-awards__years');
      const hasYears = button.dataset.hasYears === 'true';
      const isSelected = button.getAttribute('aria-selected') === 'true';
      const show = hasYears && expanded;

      wrapper?.classList.toggle('is-expanded', show);

      if (yearList) {
        yearList.hidden = !show;
      }

      if (hasYears && isSelected) {
        button.setAttribute('aria-expanded', show ? 'true' : 'false');
      } else {
        button.removeAttribute('aria-expanded');
      }
    }

    activateCategory(button) {
      if (!button) return;

      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;

      const categoryWrapper = button.closest('.press-awards__category');

      this.categoryButtons.forEach((tab) => {
        const isActive = tab === button;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        this.setCategoryYearsExpanded(tab, isActive);
      });

      this.panels.forEach((item) => {
        item.hidden = item !== panel;
      });

      this.activeCategoryButton = button;
      this.activePanel = panel;
      this.activeYear = 'all';

      this.resetYearButtons(categoryWrapper, 'all');
      this.applyYearFilter(panel, 'all');
      this.syncMobileNav(button);
      this.announceFilterResults(panel, 'all');
    }

    setYearFilter(year, panel, categoryWrapper) {
      if (!panel || !categoryWrapper) return;

      this.activeYear = year || 'all';
      this.resetYearButtons(categoryWrapper, this.activeYear);
      this.applyYearFilter(panel, this.activeYear);
      this.announceFilterResults(panel, this.activeYear);

      if (this.mobileYearSelect && this.mobileYearSelect.value !== this.activeYear) {
        this.mobileYearSelect.value = this.activeYear;
      }
    }

    resetYearButtons(categoryWrapper, activeYear = 'all') {
      if (!categoryWrapper) return;

      categoryWrapper.querySelectorAll('.press-awards__year-btn').forEach((button) => {
        const isActive = button.dataset.year === activeYear;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    applyYearFilter(panel, year) {
      const items = panel.querySelectorAll('[data-year]');
      items.forEach((item) => {
        const itemYear = item.dataset.year;
        const show = year === 'all' || itemYear === year;
        item.classList.toggle('is-filtered-out', !show);
      });
    }

    announceFilterResults(panel, year) {
      if (!this.statusRegion) return;

      const tabId = panel.getAttribute('aria-labelledby');
      const tab = tabId ? document.getElementById(tabId) : null;
      const categoryName = tab?.querySelector('.press-awards__category-label')?.textContent.trim() || 'Category';

      const items = panel.querySelectorAll('.press-awards__items [data-year]');
      let visibleCount = 0;
      items.forEach((item) => {
        if (!item.classList.contains('is-filtered-out')) {
          visibleCount += 1;
        }
      });

      const yearLabel = year === 'all' ? 'all years' : year;
      this.statusRegion.textContent = `${categoryName}: showing ${visibleCount} items for ${yearLabel}.`;
    }

    syncMobileNav(button) {
      if (!this.mobileCategorySelect) return;

      this.mobileCategorySelect.value = button.id;

      const categoryWrapper = button.closest('.press-awards__category');
      const yearNav = categoryWrapper?.querySelector('[data-year-nav]');
      const hasYears = button.dataset.hasYears === 'true' && yearNav?.querySelector('.press-awards__year-btn');

      if (this.mobileYearLabel) {
        this.mobileYearLabel.hidden = !hasYears;
      }
      if (this.mobileYearSelect) {
        this.mobileYearSelect.hidden = !hasYears;

        if (hasYears) {
          this.mobileYearSelect.innerHTML = '';
          yearNav.querySelectorAll('.press-awards__year-btn').forEach((yearButton) => {
            const option = document.createElement('option');
            option.value = yearButton.dataset.year;
            option.textContent = yearButton.textContent.trim();
            this.mobileYearSelect.appendChild(option);
          });
          this.mobileYearSelect.value = 'all';
        }
      }
    }
  }

  customElements.define('press-and-awards', PressAndAwards);
}
